import { useCallback, useRef, useState } from "react";

import { type ParagraphRun, sameRuns } from "@styled/paragraph_editor_runs";

import { type Opened, type Tip } from "./use_draft_block_clicks";

// What he's editing in place, his tip and whether his last edit saved, for
// both the Report and his template. `save` resolves to whether it changed
// anything, or undefined when it couldn't save, which keeps the editor open.
export const useParagraphEditing = (reset: () => void) => {
  const [editing, setEditing] = useState<Opened | null>(null);
  const [saved, setSaved] = useState(false);
  // Why the part he's pointing at stays as it is.
  const [tip, setTip] = useState<Tip | null>(null);
  // One save at a time: leaving the editor can ask twice.
  const busy = useRef(false);

  const open = useCallback(
    (opened: Opened) => {
      reset();
      setSaved(false);
      setEditing(opened);
    },
    [reset],
  );
  const close = useCallback(() => setEditing(null), []);

  const done = async (
    shown: ParagraphRun[] | undefined,
    runs: ParagraphRun[],
    save: () => Promise<boolean | undefined> | undefined,
  ) => {
    if (busy.current) return;
    // Unchanged: closed with nothing saved.
    if (shown && sameRuns(shown, runs)) {
      setEditing(null);
      return;
    }
    busy.current = true;
    const changed = await save()
      ?.catch(() => undefined)
      .finally(() => {
        busy.current = false;
      });
    if (changed === undefined) {
      busy.current = false;
      return;
    }
    setEditing(null);
    setSaved(changed);
  };

  return { editing, open, close, saved, tip, setTip, done };
};
